import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { Badge } from '../../../../ui/badge';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Switch } from '../../../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../ui/table';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';

interface CSVImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: { id: string; name: string; color: string | null }[];
  segments: { id: string; name: string }[];
  onImportComplete: () => void;
}

const ENGAGE_FIELDS = [
  { key: 'external_customer_id', label: 'External Customer ID', required: false },
  { key: 'first_name', label: 'First Name', required: false },
  { key: 'last_name', label: 'Last Name', required: false },
  { key: 'name', label: 'Full Name', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'status', label: 'Status', required: false },
  { key: 'joined_at', label: 'Joined At', required: false },
  { key: 'last_activity_at', label: 'Last Activity At', required: false },
  { key: 'tags', label: 'Tags', required: false },
];

const IDENTIFIER_FIELDS = ['external_customer_id', 'email', 'phone'];

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(parseLine);
  return { headers, rows };
}

export function CSVImportWizard({ open, onOpenChange, tags, segments, onImportComplete }: CSVImportWizardProps) {
  const { merchant } = useMerchant();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number; errors: { row: number; error: string }[] } | null>(null);

  const reset = () => {
    setStep(1);
    setFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
    setSelectedTagIds([]);
    setSelectedSegmentIds([]);
    setSkipInvalid(true);
    setImporting(false);
    setResult(null);
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    const text = await f.text();
    const { headers, rows } = parseCSV(text);
    if (headers.length === 0 || rows.length === 0) {
      toast.error('CSV file is empty or has no data rows');
      return;
    }
    setFile(f);
    setCsvHeaders(headers);
    setCsvRows(rows);

    // Auto-map matching columns
    const autoMap: Record<string, string> = {};
    for (const field of ENGAGE_FIELDS) {
      const match = headers.find(
        (h) => h.toLowerCase().replace(/[_\s-]/g, '') === field.key.replace(/_/g, '')
      );
      if (match) autoMap[field.key] = match;
    }
    setMapping(autoMap);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f || !f.name.endsWith('.csv')) {
      toast.error('Please drop a CSV file');
      return;
    }
    const text = await f.text();
    const { headers, rows } = parseCSV(text);
    if (headers.length === 0 || rows.length === 0) {
      toast.error('CSV file is empty or has no data rows');
      return;
    }
    setFile(f);
    setCsvHeaders(headers);
    setCsvRows(rows);
    const autoMap: Record<string, string> = {};
    for (const field of ENGAGE_FIELDS) {
      const match = headers.find(
        (h) => h.toLowerCase().replace(/[_\s-]/g, '') === field.key.replace(/_/g, '')
      );
      if (match) autoMap[field.key] = match;
    }
    setMapping(autoMap);
  }, []);

  const hasIdentifier = IDENTIFIER_FIELDS.some((f) => mapping[f]);

  const getMappedRows = () => {
    return csvRows.map((row) => {
      const mapped: Record<string, string> = {};
      for (const [engageField, csvCol] of Object.entries(mapping)) {
        const colIdx = csvHeaders.indexOf(csvCol);
        if (colIdx >= 0 && row[colIdx]) {
          mapped[engageField] = row[colIdx];
        }
      }
      return mapped;
    });
  };

  const handleImport = async () => {
    if (!merchant) return;
    setImporting(true);
    try {
      const mappedRows = getMappedRows();
      const { data, error } = await epicengageSupabase.functions.invoke('import-customers-csv', {
        body: {
          merchant_id: merchant.id,
          rows: mappedRows,
          segment_ids: selectedSegmentIds,
          tag_ids: selectedTagIds,
          skip_invalid: skipInvalid,
        },
      });
      if (error) throw error;
      setResult(data);
      setStep(5);
      toast.success(`Imported ${data.created} customers • Updated ${data.updated} • Skipped ${data.skipped}`);
      onImportComplete();
    } catch (err: any) {
      toast.error('Import failed: ' + (err.message || 'Unknown error'));
    } finally {
      setImporting(false);
    }
  };

  const getPreviewValue = (fieldKey: string, rowIdx: number) => {
    const csvCol = mapping[fieldKey];
    if (!csvCol) return '—';
    const colIdx = csvHeaders.indexOf(csvCol);
    if (colIdx < 0) return '—';
    return csvRows[rowIdx]?.[colIdx] || '—';
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Customers from CSV</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {['Upload', 'Map Columns', 'Enrich', 'Review'].map((label, idx) => (
            <div key={label} className="flex items-center gap-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                step > idx + 1 ? 'bg-primary text-primary-foreground' :
                step === idx + 1 ? 'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground'
              }`}>
                {step > idx + 1 ? '✓' : idx + 1}
              </div>
              <span className={`text-xs ${step === idx + 1 ? 'font-medium' : 'text-muted-foreground'}`}>{label}</span>
              {idx < 3 && <div className="w-6 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => document.getElementById('csv-input')?.click()}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{csvRows.length} rows found</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Drag & drop a CSV file or click to browse</p>
                </>
              )}
              <input
                id="csv-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <div className="flex justify-end">
              <Button disabled={!file} onClick={() => setStep(2)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && (
          <div className="space-y-4">
            {!hasIdentifier && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                Map at least one identifier: External Customer ID, Email, or Phone
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>EpicEngage Field</TableHead>
                  <TableHead>CSV Column</TableHead>
                  <TableHead>Preview</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ENGAGE_FIELDS.map((field) => (
                  <TableRow key={field.key}>
                    <TableCell className="font-medium">
                      {field.label}
                      {IDENTIFIER_FIELDS.includes(field.key) && (
                        <Badge variant="outline" className="ml-2 text-xs">ID</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={mapping[field.key] || '__none__'}
                        onValueChange={(v) =>
                          setMapping((m) => {
                            const next = { ...m };
                            if (v === '__none__') delete next[field.key];
                            else next[field.key] = v;
                            return next;
                          })
                        }
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="— Skip —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Skip —</SelectItem>
                          {csvHeaders.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {getPreviewValue(field.key, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={!hasIdentifier} onClick={() => setStep(3)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 3: Enrichment */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block">Assign to Segments</Label>
              <div className="flex flex-wrap gap-2">
                {segments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No segments created yet</p>
                ) : (
                  segments.map((seg) => (
                    <Badge
                      key={seg.id}
                      variant={selectedSegmentIds.includes(seg.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() =>
                        setSelectedSegmentIds((ids) =>
                          ids.includes(seg.id) ? ids.filter((i) => i !== seg.id) : [...ids, seg.id]
                        )
                      }
                    >
                      {seg.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Assign Tags</Label>
              <div className="flex flex-wrap gap-2">
                {tags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tags created yet</p>
                ) : (
                  tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant={selectedTagIds.includes(tag.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      style={selectedTagIds.includes(tag.id) && tag.color ? { backgroundColor: tag.color } : undefined}
                      onClick={() =>
                        setSelectedTagIds((ids) =>
                          ids.includes(tag.id) ? ids.filter((i) => i !== tag.id) : [...ids, tag.id]
                        )
                      }
                    >
                      {tag.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
            {mapping.tags && (
              <p className="text-sm text-muted-foreground">
                Tags from your CSV "{mapping.tags}" column will also be imported and auto-created.
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={() => setStep(4)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-muted text-center">
                <p className="text-2xl font-bold">{csvRows.length}</p>
                <p className="text-xs text-muted-foreground">Total Rows</p>
              </div>
              <div className="p-3 rounded-lg bg-muted text-center">
                <p className="text-2xl font-bold">{Object.keys(mapping).length}</p>
                <p className="text-xs text-muted-foreground">Mapped Fields</p>
              </div>
              <div className="p-3 rounded-lg bg-muted text-center">
                <p className="text-2xl font-bold">{selectedSegmentIds.length + selectedTagIds.length}</p>
                <p className="text-xs text-muted-foreground">Enrichments</p>
              </div>
            </div>

            {/* Preview table */}
            <div className="border rounded-lg overflow-x-auto max-h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    {Object.keys(mapping).map((field) => (
                      <TableHead key={field}>{ENGAGE_FIELDS.find((f) => f.key === field)?.label || field}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvRows.slice(0, 20).map((_, rowIdx) => (
                    <TableRow key={rowIdx}>
                      <TableCell className="text-muted-foreground">{rowIdx + 1}</TableCell>
                      {Object.keys(mapping).map((field) => (
                        <TableCell key={field} className="text-sm truncate max-w-[150px]">
                          {getPreviewValue(field, rowIdx)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {csvRows.length > 20 && (
              <p className="text-xs text-muted-foreground text-center">Showing first 20 of {csvRows.length} rows</p>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={skipInvalid} onCheckedChange={setSkipInvalid} />
              <Label className="text-sm">Import valid rows only (skip errors)</Label>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</> : `Import ${csvRows.length} Customers`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Result */}
        {step === 5 && result && (
          <div className="space-y-4 text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <h3 className="text-lg font-semibold">Import Complete</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-2xl font-bold text-primary">{result.created}</p>
                <p className="text-xs text-muted-foreground">Created</p>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-2xl font-bold">{result.updated}</p>
                <p className="text-xs text-muted-foreground">Updated</p>
              </div>
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-2xl font-bold text-muted-foreground">{result.skipped}</p>
                <p className="text-xs text-muted-foreground">Skipped</p>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="text-left text-sm">
                <p className="font-medium text-destructive mb-1">{result.errors.length} errors:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {result.errors.slice(0, 10).map((e, i) => (
                    <p key={i} className="text-muted-foreground">Row {e.row + 1}: {e.error}</p>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
